import { useState } from 'react';
import { Download, X, Share } from 'lucide-react';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { useTranslation } from '../lib/i18n';
import { Button } from './ui/Button';

export default function InstallBanner() {
  const { canInstall, promptInstall, dismissPrompt, showIOSHint } = usePWAInstall();
  const { t } = useTranslation();
  const [showIOSModal, setShowIOSModal] = useState(false);

  if (!canInstall && !showIOSHint) return null;

  return (
    <>
      {canInstall && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg animate-slide-down">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-white/20 flex-shrink-0">
                <Download className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm sm:text-base truncate">{t('pwa.bannerTitle')}</p>
                <p className="text-xs sm:text-sm text-white/80 truncate">{t('pwa.bannerDesc')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="secondary"
                onClick={promptInstall}
                className="bg-white text-blue-700 hover:bg-blue-50"
              >
                <Download className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">{t('pwa.install')}</span>
              </Button>
              <button
                onClick={dismissPrompt}
                aria-label={t('pwa.later')}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showIOSHint && !canInstall && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg animate-slide-down">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-full bg-white/20 flex-shrink-0">
                <Share className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm sm:text-base truncate">{t('pwa.iosTitle')}</p>
                <p className="text-xs sm:text-sm text-white/80 truncate">{t('pwa.iosStep1')} → {t('pwa.iosStep2')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowIOSModal(true)}
                className="bg-white text-blue-700 hover:bg-blue-50"
              >
                <span className="hidden sm:inline">{t('pwa.install')}</span>
              </Button>
              <button
                onClick={dismissPrompt}
                aria-label={t('pwa.later')}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showIOSModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowIOSModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <Share className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t('pwa.iosTitle')}</h3>
              </div>
              <button
                onClick={() => setShowIOSModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-3">
              {[
                { icon: <Share className="w-4 h-4" />, text: t('pwa.iosStep1') },
                { icon: <Download className="w-4 h-4" />, text: t('pwa.iosStep2') },
                { icon: <Download className="w-4 h-4" />, text: t('pwa.iosStep3') },
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 flex-shrink-0">
                    {step.icon}
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{step.text}</span>
                </div>
              ))}
            </div>
            <Button
              onClick={() => setShowIOSModal(false)}
              className="w-full mt-5"
            >
              {t('pwa.iosClose')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
